import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, ShieldCheck, Crown, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const STATUS_STYLE = {
  pending: "border-warning/50 text-warning",
  active: "border-success/50 text-success",
};

export default function Admin() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.getSubscribers().then(setRows).catch(() => toast.error("Chargement impossible")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const activate = async (email, plan) => {
    setBusy(email + plan);
    try {
      await api.activate(email, plan);
      toast.success(`${email} activé en ${plan.toUpperCase()}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'activation");
    } finally {
      setBusy("");
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="px-4 md:px-8 py-16 text-center" data-testid="admin-forbidden">
        <ShieldCheck className="h-10 w-10 text-primary mx-auto mb-3" />
        <p className="font-mono text-sm text-muted-foreground">Accès réservé à l'administrateur.</p>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-8 max-w-[1200px] mx-auto space-y-6 animate-fade-in" data-testid="admin-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-primary tracking-widest mb-2">// MODULE 08 · ADMIN</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3"><Users className="h-8 w-8 text-primary" /> Abonnés</h1>
          <p className="text-muted-foreground mt-1 text-sm">Gère les demandes d'abonnement et active les accès après paiement.</p>
        </div>
        <Button variant="outline" onClick={load} className="font-mono text-xs gap-2" data-testid="admin-refresh-btn">
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} /> ACTUALISER
        </Button>
      </div>

      <Card className="bg-card/60 border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border font-mono text-[10px] tracking-widest text-muted-foreground">// DEMANDES D'ABONNEMENT ({rows.length})</div>
        <div className="divide-y divide-border">
          {!loading && rows.length === 0 && (
            <div className="px-5 py-10 text-center text-muted-foreground font-mono text-sm" data-testid="admin-empty">// aucune demande pour l'instant</div>
          )}
          {rows.map((r) => (
            <div key={r.user_id || r.email} className="px-5 py-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`sub-row-${r.email}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{r.name || r.email}</div>
                <div className="font-mono text-xs text-muted-foreground truncate">{r.email}</div>
              </div>
              <div className="flex items-center gap-2 text-xs font-mono">
                <span className="text-muted-foreground">demande :</span>
                <Badge variant="outline" className="border-primary/40 text-primary uppercase">{r.plan}</Badge>
              </div>
              <div className="flex items-center gap-2 text-xs font-mono">
                <span className="text-muted-foreground">actuel :</span>
                <Badge variant="outline" className="border-border uppercase">{r.current_plan || "free"}</Badge>
              </div>
              <Badge variant="outline" className={cn("font-mono text-[10px] uppercase", STATUS_STYLE[r.status] || "border-border")}>{r.status}</Badge>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={busy === r.email + r.plan} onClick={() => activate(r.email, r.plan)} className="font-mono text-[10px] gap-1.5 border-success/50 text-success hover:bg-success/10" data-testid={`activate-${r.email}-btn`}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> ACTIVER {r.plan?.toUpperCase()}
                </Button>
                {r.current_plan !== "free" && (
                  <Button size="sm" variant="ghost" disabled={busy === r.email + "free"} onClick={() => activate(r.email, "free")} className="font-mono text-[10px] text-muted-foreground" data-testid={`downgrade-${r.email}-btn`}>
                    REVENIR FREE
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="bg-card/60 border-primary/30 p-5 flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center shrink-0"><Crown className="h-5 w-5 text-primary" /></div>
        <div>
          <div className="font-mono text-[10px] tracking-widest text-primary mb-1">// RAPPEL</div>
          <div className="font-semibold text-sm">Activation manuelle</div>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">Active un abonné après confirmation du paiement. L'activation met à jour sa formule et débloque immédiatement les outils correspondants.</p>
        </div>
      </Card>
    </div>
  );
}
